import { gql } from '@apollo/client';
import PluginEditor from '@draft-js-plugins/editor';
import { useRouter } from 'next/router';
import * as React from 'react';

import { IExpandProps, useExpand } from '@/components/Post/hooks/useExpand';

import {
  useReplyActions_CommentFragment,
  useReplyActions_CommentFragment_comments,
} from './graphql-types/useReplyActions_CommentFragment';

import {
  COMMENT_QUERY_PARAMETER_NAME,
  REPLY_TO_COMMENT_NAME,
} from '../../../utils';
import { CreateReply_UserFragmentAST } from '../components/CreateReply';
import { CreateReply_UserFragment } from '../components/CreateReply/graphql-types/CreateReply_UserFragment';

const EXPAND_INCREMENT = 3;

const getIndexOfFirstComment = (options: {
  comments: useReplyActions_CommentFragment_comments[];
  commentIdToShow?: string | null;
}): number => {
  const { comments, commentIdToShow } = options;
  if (!commentIdToShow) {
    return -1;
  }

  return comments.findIndex((comment) => comment._id === commentIdToShow);
};

export const useReplyActions = (options: {
  comment: useReplyActions_CommentFragment;
}): {
  ref: React.RefObject<PluginEditor>;
  mentionAuthorAndFocusAndScrollToRef: (
    comment: Pick<useReplyActions_CommentFragment, 'author'>
  ) => void;
  replyMentionedUser: CreateReply_UserFragment | null;
  clearReplyMentionedUser: () => void;
  expandOrMention: (comment: useReplyActions_CommentFragment) => void;
} & IExpandProps => {
  const router = useRouter();
  const initialCommentIdToShow =
    router.query[COMMENT_QUERY_PARAMETER_NAME]?.toString();
  const replyToComment = router.query[REPLY_TO_COMMENT_NAME]?.toString();

  const { comment } = options;
  const [replyMentionedUser, setReplyMentionedUser] =
    React.useState<CreateReply_UserFragment | null>(null);
  const ref = React.useRef<PluginEditor>(null);

  const startingCommentIndex = getIndexOfFirstComment({
    comments: options.comment.comments.slice(0).reverse(),
    commentIdToShow: initialCommentIdToShow || replyToComment,
  });

  const { startIndex, expand, numHidden } = useExpand({
    items: comment.comments,
    initialNumShown: startingCommentIndex !== -1 ? startingCommentIndex + 1 : 1,
  });

  const mentionAuthorAndFocusAndScrollToRef = React.useCallback(
    (comment: useReplyActions_CommentFragment): void => {
      if (replyMentionedUser) {
        ref.current?.focus();
        // @ts-ignore
        ref.current?.editor?.editor?.parentElement?.scrollIntoView?.({
          behavior: 'smooth',
          block: 'center',
        });
      }
      setReplyMentionedUser(comment.author);
    },
    [replyMentionedUser, setReplyMentionedUser]
  );

  React.useEffect(() => {
    [comment, ...comment.comments].forEach((comment) => {
      if (replyToComment === comment._id) {
        mentionAuthorAndFocusAndScrollToRef(
          comment as useReplyActions_CommentFragment
        );
      }
    });
  }, [replyToComment, comment, mentionAuthorAndFocusAndScrollToRef]);

  return {
    ref,
    mentionAuthorAndFocusAndScrollToRef,
    replyMentionedUser,
    startIndex,
    expand,
    numHidden,
    clearReplyMentionedUser: () => setReplyMentionedUser(null),
    expandOrMention: (comment) => {
      comment.comments.length > 0 && numHidden > 0
        ? expand(EXPAND_INCREMENT)
        : mentionAuthorAndFocusAndScrollToRef(comment);
    },
  };
};

useReplyActions.commentFragment = gql`
  fragment useReplyActions_CommentFragment on Comment {
    _id
    author {
      _id
      ...CreateReply_UserFragment
    }
    comments {
      _id
      author {
        _id
        ...CreateReply_UserFragment
      }
    }
  }
  ${CreateReply_UserFragmentAST}
`;
